import React, { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";

/**
 * Floating mascot "Back to Top" button.
 * - Hidden until the user scrolls past ~400 px.
 * - Subtle pulse animation to catch the eye.
 * - Mascot icon + up-arrow + "TOP" label make the function unmistakable.
 * - Smooth-scrolls to the top of the page on click.
 */
export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      aria-label="Back to top"
      data-testid="back-to-top-btn"
      onClick={scrollTop}
      className={`group fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-50 flex flex-col items-center transition-all duration-300 ease-out ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <span className="relative inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-[#0B0B0B] border-2 border-[#5FBDD6] shadow-[0_8px_30px_rgba(95,189,214,0.45)] group-hover:-translate-y-1 group-hover:scale-105 group-hover:shadow-[0_14px_40px_rgba(95,189,214,0.65)] group-active:scale-95 transition-all duration-300"
        style={{ animation: visible ? "btwPulse 2.4s ease-in-out infinite" : "none" }}
      >
        {/* Pulsing halo */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(95,189,214,0.45), transparent 75%)",
            animation: visible ? "btwHalo 2.4s ease-in-out infinite" : "none",
          }}
        />
        {/* Mascot */}
        <img
          src="/brand/btw-mascot.png"
          alt=""
          draggable={false}
          className="relative h-9 w-9 sm:h-11 sm:w-11 object-contain select-none drop-shadow-[0_3px_8px_rgba(0,0,0,0.6)] group-hover:scale-110 transition-transform"
        />
        {/* Up-arrow badge */}
        <span
          aria-hidden="true"
          className="absolute -top-1.5 -right-1.5 inline-flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-[#A3D900] text-[#0B0B0B] border-2 border-[#0B0B0B] shadow-[0_2px_8px_rgba(0,0,0,0.45)] group-hover:scale-110 transition-transform"
        >
          <ChevronUp size={14} strokeWidth={3} />
        </span>
      </span>

      {/* "TOP" label pill */}
      <span
        aria-hidden="true"
        className="mt-1.5 inline-block rounded-full bg-[#5FBDD6] text-[#0B0B0B] font-extrabold text-[9px] sm:text-[10px] tracking-[0.28em] uppercase px-2.5 py-0.5 shadow-[0_4px_12px_rgba(95,189,214,0.5)] group-hover:bg-[#A3D900] transition-colors"
      >
        Top
      </span>

      <style>{`
        @keyframes btwPulse {
          0%, 100% { box-shadow: 0 8px 30px rgba(95,189,214,0.45); transform: translateY(0) scale(1); }
          50%      { box-shadow: 0 12px 38px rgba(95,189,214,0.85); transform: translateY(-2px) scale(1.04); }
        }
        @keyframes btwHalo {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50%      { opacity: 0.75; transform: scale(1.18); }
        }
      `}</style>
    </button>
  );
}
